import React from "react";
import { Row, Col } from "react-bootstrap";
import { DashboardData } from "../../hooks/useDashboardData";
import useAlerts from "../../hooks/useAlerts";
import Nav from "../Nav";
import UpdateText from "./UpdateText";
import Aggregate from "./AggregateMetrics/Aggregate";
import Daily from "./DailyMetrics/Daily";
import Rate from "./DailyMetrics/Rate";
import Demographics from "./Demographics/Demographics";
import PresumptiveCounts from "./ProbableCounts/PresumptiveCounts";
import Hospitalization from "./Hospitalization/Hospitalization";
import CountyMap from "./Map/CountyMap";
import Variants from "./Variants/Variants";
import BedVentilator from "./BedVent/BedVentilator";
import Misc from "./MISC/Misc";
import ChartHeader from "../../components/CardHeader";
import Filter from "./Filter/Filter";
import {
  COLOR_POSITIVE,
  COLOR_DEATHS,
  COLOR_TESTED,
  COLOR_POSITIVE_DARK,
  POSITIVE_TEST_RATE,
  POSITIVE_TEST_RATE_MOVING,
} from "../../GlobalColors";
import { refAreaCalc } from "../../utils/DataConversion";

interface IsdhProps {
  dashboardData: DashboardData | undefined;
  reportDate: string;
  currentFilter: App.CurrentFilter;
  setCurrentFilter: (filter: App.CurrentFilter) => void;
  dateFilter: [number, number] | undefined;
  setDateFilter: (filter: [number, number] | undefined) => void;
  outliers: Data.Outlier[] | undefined;
  metricMapDate: string;
  resetFilter: () => void;
}

export default function Isdh(props: IsdhProps) {
  const {
    dashboardData,
    reportDate,
    currentFilter,
    dateFilter,
    outliers,
    setCurrentFilter,
    setDateFilter,
    metricMapDate,
    resetFilter,
  } = props;

  const { renderAlerts } = useAlerts({
    type: "dashboard",
  });

  return (
    <React.Fragment>
      <Nav />
      <UpdateText reportDate={reportDate} />
      {renderAlerts}
      {dashboardData ? (
        <Filter setDateFilter={setDateFilter} reportDate={reportDate} />
      ) : null}
      <Row className="m-stats-cards">
        {dashboardData && dashboardData.agg ? (
          <Aggregate
            currentFilter={currentFilter}
            dateFilter={dateFilter !== undefined}
            data={dashboardData.agg}
          />
        ) : null}
      </Row>
      <Row>
        <Col xs={12} sm={12} md={12} lg={12} className="m-map card-wrapper">
          <div className="map-card card h-100 mb-0">
            {dashboardData && dashboardData.map ? (
              <CountyMap
                data={dashboardData.map}
                resetFilter={resetFilter}
                currentFilter={currentFilter}
                setCurrentFilter={setCurrentFilter}
                metricMapDate={metricMapDate}
                outliers={outliers}
              />
            ) : null}
          </div>
        </Col>
        <Col xs={12} md={12} lg={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData && reportDate ? (
              <React.Fragment>
                <ChartHeader
                  title="Positive Cases"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Daily
                  dataKeys={[
                    ["newCases", "newCases"],
                    ["existingCases", "existingCases"],
                    ["caseMoving", "caseMoving"],
                  ]}
                  title={
                    !currentFilter.filters.length
                      ? "Statewide Positive Cases by Day"
                      : currentFilter.displayName + " Positive Cases by Day"
                  }
                  barLabel="Positive Case"
                  data={dashboardData.daily}
                  color={COLOR_POSITIVE}
                  darkColor={COLOR_POSITIVE_DARK}
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
        <Col xs={12} md={12} lg={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData && reportDate ? (
              <React.Fragment>
                <ChartHeader
                  title="Tests"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Daily
                  title={
                    !currentFilter.filters.length
                      ? "Statewide Tests by Day"
                      : currentFilter.displayName + " Tests by Day"
                  }
                  dataKeys={[
                    ["newTests", "newTestAdmin"],
                    ["existingTests", "existingTestAdmin"],
                    ["testMoving", "testAdminMoving"],
                  ]}
                  barLabel={"Test"}
                  showIndividualToggle
                  data={dashboardData.daily}
                  color={COLOR_TESTED}
                  startPre={refAreaCalc(reportDate)}
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
        <Col xs={12} md={12} lg={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData && reportDate ? (
              <React.Fragment>
                <ChartHeader
                  title="Deaths"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Daily
                  title={
                    !currentFilter.filters.length
                      ? "Statewide Deaths by Day"
                      : currentFilter.displayName + " Deaths by Day"
                  }
                  dataKeys={[
                    ["newDeaths", "newDeaths"],
                    ["existingDeaths", "existingDeaths"],
                    ["deathMoving", "deathMoving"],
                  ]}
                  barLabel="Death"
                  data={dashboardData.daily}
                  color={COLOR_DEATHS}
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
        <Col xs={12} md={12} lg={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData && reportDate ? (
              <React.Fragment>
                <ChartHeader
                  title="Positivity"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Rate
                  reportDate={reportDate || ""}
                  currentFilter={currentFilter}
                  dataKeyFormalNames={[
                    "Positive Test Rate",
                    "Positive Rate 7-Day Average",
                  ]}
                  dataKeys={[
                    ["testRate", "testRateMoving"],
                    ["testAdminRate", "testAdminRateMoving"],
                  ]}
                  color={POSITIVE_TEST_RATE}
                  darkColor={POSITIVE_TEST_RATE_MOVING}
                  data={dashboardData.daily}
                  startPre={refAreaCalc(reportDate)}
                  percent
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={12} lg={12} className="m-demographics card-wrapper">
          <div className="m-tabs card mb-0">
            {dashboardData ? (
              <Demographics
                currentFilter={currentFilter}
                data={dashboardData.demo}
              />
            ) : null}
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={12} lg={12} className="m-demographics card-wrapper">
          <div className="m-tabs card mb-0">
            {dashboardData ? (
              <BedVentilator
                currentFilter={currentFilter}
                data={dashboardData.bedvent}
                dateFilter={dateFilter !== undefined}
              />
            ) : null}
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={12} lg={12} className="m-demographics card-wrapper">
          <div className="m-tabs card mb-0">
            {dashboardData && reportDate ? (
              <Hospitalization
                reportDate={reportDate}
                currentFilter={currentFilter}
                data={dashboardData.hospital}
                dateFilter={dateFilter !== undefined}
              />
            ) : null}
          </div>
        </Col>
      </Row>
      <Row className="h-100">
        <Col xs={12} md={12} lg={6} className="card-wrapper mb-3">
          {dashboardData ? (
            <PresumptiveCounts data={dashboardData.probable} />
          ) : null}
        </Col>
        <Col xs={12} md={12} lg={6} className="card-wrapper mb-3">
          {dashboardData ? (
            <Variants
              data={dashboardData.variants}
              dataByWeek={dashboardData.variantsByWeek}
            />
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col className="card-wrapper mb-3">
          {dashboardData ? <Misc {...dashboardData.misc} /> : null}
        </Col>
      </Row>
      <Row>
        <Col className="text-center">
          <hr />
          <p>
            <em>
              All data displayed is preliminary and subject to change as more
              information is reported to IDOH. Tests are displayed by the date
              the test was performed and deaths are displayed by the date the
              death occurred.
            </em>
          </p>
          <p>
            <em>
              Expect historical data to change as data is reported to IDOH.
            </em>
          </p>
        </Col>
      </Row>
    </React.Fragment>
  );
}
